import React, { useState, useMemo } from 'react';
import { CreditCard, FileText, Filter, CheckCircle, Clock, XCircle, DollarSign, Wallet } from 'lucide-react';
import { formatCurrency, formatDate } from '../../../utils/helpers';

const MobileDuesView = ({
    playerData,
    expenses,
    draftExpenses = [],
    walletTransactions,
    netBalance,
    amountPaid,
    reimbursementsOwed,
    getBalanceDueBreakdown,
    setShowPaymentQR,
    setPaymentAmount,
    setView,
    onEditDraft,
    pendingExpenses = [],
    paymentSubmissions = [],
    setShowPendingExpensesModal
}) => {
    const [selectedTransaction, setSelectedTransaction] = useState(null);

    // Combine and sort ledger items
    const ledgerItems = useMemo(() => {
        let items = [];

        // 1. Add Expenses
        expenses.filter(exp => exp.paidBy === (playerData?.userId || playerData?.id)).forEach(exp => {
            let statusObj = { text: 'Submitted', color: 'bg-yellow-100 text-yellow-800' };
            if (exp.status === 'approved') statusObj = { text: 'Approved', color: 'bg-green-100 text-green-800' };
            else if (exp.status === 'settled') statusObj = { text: 'Settled', color: 'bg-blue-100 text-blue-800' };
            else if (exp.status === 'rejected') statusObj = { text: 'Rejected', color: 'bg-red-100 text-red-800' };

            items.push({
                id: `exp-${exp.id}`,
                type: 'expense',
                title: exp.description || 'Expense',
                amount: exp.amount,
                date: new Date(exp.incurredDate || exp.createdAt || Date.now()),
                statusObj,
                original: exp,
                isMyExpense: true
            });
        });

        // 1.5 Add Draft Expenses
        draftExpenses.filter(draft => draft.paidBy === (playerData?.userId || playerData?.id) || draft.userId === (playerData?.userId || playerData?.id)).forEach(draft => {
            items.push({
                id: `draft-${draft.id}`,
                type: 'expense',
                title: draft.description || 'Draft Expense',
                amount: draft.amount || 0,
                date: new Date(draft.incurredDate || draft.updatedAt || draft.createdAt || Date.now()),
                statusObj: { text: 'Draft', color: 'bg-gray-200 text-gray-700' },
                original: draft,
                isMyExpense: true,
                isDraft: true
            });
        });

        // 2. Add Wallet Transactions (Payments / Dues)
        walletTransactions.filter(wt => wt.userId === (playerData?.userId || playerData?.id) || wt.playerId === (playerData?.userId || playerData?.id)).forEach(wt => {
            let statusObj = { text: 'Pending Approval', color: 'bg-yellow-100 text-yellow-800' };
            if (wt.status === 'approved') statusObj = { text: 'Approved', color: 'bg-green-100 text-green-800' };
            else if (wt.status === 'rejected') statusObj = { text: 'Rejected', color: 'bg-red-100 text-red-800' };

            items.push({
                id: `wt-${wt.id}`,
                type: wt.type === 'payment' ? 'due' : 'transaction',
                title: wt.description || (wt.type === 'payment' ? 'Payment Made' : 'Transaction'),
                amount: wt.amount,
                date: new Date(wt.paymentDate || wt.createdAt || Date.now()),
                statusObj,
                original: wt
            });
        });

        // 3. Add Breakdown Items from `getBalanceDueBreakdown` (Tournament Fees, Umpiring, etc.)
        const breakdown = getBalanceDueBreakdown ? getBalanceDueBreakdown() : { items: [] };
        breakdown.items.forEach(item => {
            // These don't directly correspond to raw DB rows, they are calculated dues
            items.push({
                id: `bd-${item.id}`,
                type: item.type === 'tournament_fee' ? 'tournament' : item.type === 'umpiring' ? 'umpiring' : 'due',
                title: item.description,
                amount: item.amount,
                date: new Date(item.date || Date.now()),
                statusObj: { text: 'Owed', color: 'bg-red-100 text-red-800' },
                original: item
            });
        });

        // Sort descending by date
        return items.sort((a, b) => b.date - a.date);
    }, [expenses, walletTransactions, getBalanceDueBreakdown, playerData, draftExpenses]);


    return (
        <div className="pb-safe max-w-lg mx-auto md:hidden">
            <div className="px-4 pt-6 pb-24 space-y-6">

                {/* Dashboard Money Box */}
                <div className={`p-6 rounded-2xl shadow-lg border relative overflow-hidden ${netBalance > 0
                    ? 'bg-gradient-to-br from-red-50 to-red-100 border-red-200'
                    : netBalance < 0
                        ? 'bg-gradient-to-br from-blue-50 to-blue-100 border-blue-200'
                        : 'bg-gradient-to-br from-green-50 to-green-100 border-green-200'
                    }`}>
                    <div className="relative z-10">
                        <p className="text-sm font-semibold text-gray-600 mb-1 uppercase tracking-wider">
                            {netBalance > 0 ? 'Total Balance Due' : netBalance < 0 ? 'Club Owes You' : 'All Settled'}
                        </p>
                        <h2 className={`text-4xl font-bold tracking-tight ${netBalance > 0 ? 'text-red-700' : netBalance < 0 ? 'text-blue-700' : 'text-green-700'
                            }`}>
                            {formatCurrency(Math.abs(netBalance))}
                        </h2>

                        {/* Show pending payments if exists */}
                        {paymentSubmissions.filter(p => p.status === 'pending').length > 0 && (() => {
                            const pendingAmount = paymentSubmissions
                                .filter(p => p.status === 'pending')
                                .reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);

                            return (
                                <p className="text-xs text-gray-500 italic mt-1 text-center">
                                    Payment pending approval ${pendingAmount.toFixed(2)}
                                </p>
                            );
                        })()}

                        {/* Show pending expenses count and amount */}
                        {pendingExpenses.length > 0 && (() => {
                            const pendingExpenseTotal = pendingExpenses.reduce((sum, exp) => sum + (parseFloat(exp.amount) || 0), 0);

                            return (
                                <p
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (setShowPendingExpensesModal) {
                                            setShowPendingExpensesModal(true);
                                        }
                                    }}
                                    className="text-xs text-yellow-600 mt-1 hover:text-yellow-700 cursor-pointer font-medium text-center"
                                >
                                    {pendingExpenses.length} expense{pendingExpenses.length !== 1 ? 's' : ''} awaiting approval (${pendingExpenseTotal.toFixed(2)})
                                </p>
                            );
                        })()}

                        <div className="mt-4 pt-4 border-t border-black/5 flex justify-between text-sm">
                            <div>
                                <p className="text-black/50 font-medium tracking-wide">Payments</p>
                                <p className="font-bold text-gray-800 text-lg">{formatCurrency(amountPaid)}</p>
                            </div>
                            <div className="text-right">
                                <p className="text-black/50 font-medium tracking-wide">Reimbursements</p>
                                <p className="font-bold text-gray-800 text-lg">{formatCurrency(reimbursementsOwed)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Primary Actions */}
                <div className="grid grid-cols-2 gap-4">
                    <button
                        onClick={() => {
                            setPaymentAmount(netBalance > 0 ? netBalance.toString() : '');
                            setShowPaymentQR(true);
                        }}
                        className="flex flex-col flex-1 items-center justify-center gap-2 p-4 bg-gradient-to-br from-blue-600 to-indigo-600 text-white rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-95"
                    >
                        <Wallet size={24} className="opacity-90" />
                        <span className="font-bold tracking-wide">Pay Now</span>
                    </button>

                    <button
                        onClick={() => setView('add-expense')}
                        className="flex flex-col flex-1 items-center justify-center gap-2 p-4 bg-gradient-to-br from-green-500 to-emerald-600 text-white rounded-2xl shadow-md hover:shadow-lg transition-all active:scale-95"
                    >
                        <FileText size={24} className="opacity-90" />
                        <span className="font-bold tracking-wide">File Expense</span>
                    </button>
                </div>

                {/* Fees Owed — matches desktop Balance Due breakdown */}
                {(() => {
                    const breakdown = getBalanceDueBreakdown ? getBalanceDueBreakdown() : { items: [], total: 0 };
                    const registrationItems = breakdown.items.filter(i => i.type === 'registration');
                    const gameItems = breakdown.items.filter(i => i.type === 'game_expense' || i.type === 'late_fee');
                    const tournamentFeeItems = breakdown.items.filter(i => i.type === 'tournament_fee');
                    const merchandiseItems = breakdown.items.filter(i => i.type === 'merchandise');

                    const payments = walletTransactions.filter(t =>
                        (t.userId === (playerData?.userId || playerData?.id)) &&
                        t.type === 'payment' && t.status === 'approved' && t.amount < 0 && !t.isDonation
                    );
                    const waivers = walletTransactions.filter(t =>
                        (t.userId === (playerData?.userId || playerData?.id)) &&
                        t.type === 'waiver' && t.status === 'approved' && t.amount < 0
                    );
                    const pendingPayments = paymentSubmissions.filter(p => p.status === 'pending');
                    const reimbursements = walletTransactions.filter(t =>
                        (t.userId === (playerData?.userId || playerData?.id)) &&
                        t.status === 'approved' &&
                        (t.type === 'expense_reimbursement' || t.type === 'reimbursement_paid')
                    );

                    const totalOwed = breakdown.total || 0;
                    const totalPaid = [...payments, ...waivers].reduce((sum, t) => sum + Math.abs(t.amount || 0), 0);

                    return (
                        <div className="space-y-4">
                            {/* Fees Owed */}
                            <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                                    <span className="w-3 h-3 rounded-full bg-red-500 flex-shrink-0" />
                                    <h3 className="text-base font-bold text-gray-900">Fees Owed</h3>
                                    <span className="ml-auto font-bold text-red-600">{formatCurrency(totalOwed)}</span>
                                </div>

                                {breakdown.items.length === 0 ? (
                                    <div className="p-6 text-center text-gray-400 text-sm">No fees owed</div>
                                ) : (
                                    <div className="divide-y divide-gray-50">
                                        {/* Annual Membership / Registration */}
                                        {registrationItems.map(item => (
                                            <div key={item.id} className="flex justify-between items-center px-4 py-3 bg-red-50/40">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">{item.description}</p>
                                                    {item.date && <p className="text-xs text-gray-500 mt-0.5">{formatDate(item.date)}</p>}
                                                </div>
                                                <span className="font-bold text-red-600 text-sm whitespace-nowrap">+{formatCurrency(Math.abs(item.amount))}</span>
                                            </div>
                                        ))}

                                        {/* Per-game expenses (ground fees, snacks, etc.) */}
                                        {gameItems.map(item => (
                                            <div key={item.id} className="flex justify-between items-center px-4 py-3">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">{item.description}</p>
                                                    {item.gameName && <p className="text-xs text-gray-500 mt-0.5">{item.gameName}</p>}
                                                    {item.isSuperSub && <span className="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded mt-0.5 inline-block">Super Sub (50%)</span>}
                                                </div>
                                                <span className="font-bold text-red-600 text-sm whitespace-nowrap">+{formatCurrency(Math.abs(item.amount))}</span>
                                            </div>
                                        ))}

                                        {/* Tournament Fees */}
                                        {tournamentFeeItems.map(item => (
                                            <div key={item.id} className="flex justify-between items-center px-4 py-3 bg-purple-50/40">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">🏆 {item.description}</p>
                                                    {item.gameName ? <p className="text-xs text-gray-500 mt-0.5">{item.gameName}</p> : item.gamesPlayed && <p className="text-xs text-gray-500 mt-0.5">{item.gamesPlayed} game{item.gamesPlayed !== 1 ? 's' : ''} played</p>}
                                                </div>
                                                <span className="font-bold text-purple-700 text-sm whitespace-nowrap">+{formatCurrency(Math.abs(item.amount))}</span>
                                            </div>
                                        ))}

                                        {/* Merchandise */}
                                        {merchandiseItems.map(item => (
                                            <div key={item.id} className="flex justify-between items-center px-4 py-3">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">🛍️ {item.description}</p>
                                                    {item.date && <p className="text-xs text-gray-500 mt-0.5">{formatDate(item.date)}</p>}
                                                </div>
                                                <span className="font-bold text-red-600 text-sm whitespace-nowrap">+{formatCurrency(Math.abs(item.amount))}</span>
                                            </div>
                                        ))}

                                        {/* Total row */}
                                        <div className="flex justify-between items-center px-4 py-3 bg-gray-50 border-t border-gray-200">
                                            <span className="font-semibold text-gray-700 text-sm">Total Owed</span>
                                            <span className="font-bold text-red-600">{formatCurrency(totalOwed)}</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Payments Made */}
                            {(payments.length > 0 || waivers.length > 0 || pendingPayments.length > 0) && (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-green-500 flex-shrink-0" />
                                        <h3 className="text-base font-bold text-gray-900">Payments Made</h3>
                                        <span className="ml-auto font-bold text-green-600">-{formatCurrency(totalPaid)}</span>
                                    </div>
                                    <div className="divide-y divide-gray-50">
                                        {payments.map(t => (
                                            <div key={t.id} className="flex justify-between items-center px-4 py-3">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">{t.description || 'Payment'}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {t.paymentMethod && `${t.paymentMethod} · `}{formatDate(new Date(t.paymentDate || t.createdAt))}
                                                    </p>
                                                </div>
                                                <span className="font-bold text-green-600 text-sm whitespace-nowrap">-{formatCurrency(Math.abs(t.amount))}</span>
                                            </div>
                                        ))}
                                        {waivers.map(t => (
                                            <div key={t.id} className="flex justify-between items-center px-4 py-3 bg-purple-50/30">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">🎁 {t.waiverReason || t.description || 'Fee Waived'}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">{formatDate(new Date(t.createdAt))}</p>
                                                </div>
                                                <span className="font-bold text-purple-600 text-sm whitespace-nowrap">-{formatCurrency(Math.abs(t.amount))}</span>
                                            </div>
                                        ))}
                                        {pendingPayments.map(p => (
                                            <div key={p.id} className="flex justify-between items-center px-4 py-3 bg-yellow-50/40">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">{p.paymentMethod || 'Payment'} · Pending Approval</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">{formatDate(new Date(p.paymentDate || p.submittedAt))}</p>
                                                </div>
                                                <span className="font-bold text-yellow-600 text-sm whitespace-nowrap">-{formatCurrency(Math.abs(p.amount))}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Reimbursements (Club Owes You) */}
                            {reimbursements.length > 0 && (
                                <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
                                    <div className="p-4 border-b border-gray-100 flex items-center gap-2">
                                        <span className="w-3 h-3 rounded-full bg-blue-500 flex-shrink-0" />
                                        <h3 className="text-base font-bold text-gray-900">Reimbursements (Club Owes You)</h3>
                                        <span className="ml-auto font-bold text-blue-600">-{formatCurrency(reimbursementsOwed)}</span>
                                    </div>
                                    <div className="divide-y divide-gray-50">
                                        {reimbursements.map(t => (
                                            <div key={t.id} className="flex justify-between items-center px-4 py-3 bg-blue-50/20">
                                                <div className="flex-1 min-w-0 pr-3">
                                                    <p className="font-medium text-gray-800 text-sm">{t.description || 'Reimbursement'}</p>
                                                    <p className="text-xs text-gray-500 mt-0.5">
                                                        {formatDate(new Date(t.createdAt))}
                                                    </p>
                                                </div>
                                                <span className="font-bold text-blue-600 text-sm whitespace-nowrap">-{formatCurrency(Math.abs(t.amount))}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    );
                })()}


            {/* Transaction Details Modal */}
            {selectedTransaction && (
                <div className="fixed inset-0 bg-black/60 z-[100] flex items-center justify-center p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-2xl w-full max-w-sm overflow-hidden shadow-2xl animate-in zoom-in-95 duration-200">
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
                            <h3 className="font-bold text-gray-900 text-lg">Transaction Details</h3>
                            <button
                                onClick={() => setSelectedTransaction(null)}
                                className="p-1.5 hover:bg-gray-200 rounded-full transition-colors text-gray-500"
                            >
                                <XCircle size={22} />
                            </button>
                        </div>

                        {/* Body */}
                        <div className="p-5 space-y-4">
                            <div>
                                <p className="text-sm text-gray-500 font-medium mb-1">Description</p>
                                <p className="font-semibold text-gray-900 text-lg">{selectedTransaction.title}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-sm text-gray-500 font-medium mb-1">Date</p>
                                    <p className="font-medium text-gray-800">{formatDate(selectedTransaction.date)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-gray-500 font-medium mb-1">Status</p>
                                    <span className={`inline-flex px-2 py-0.5 rounded-md text-xs font-bold ${selectedTransaction.statusObj.color}`}>
                                        {selectedTransaction.statusObj.text}
                                    </span>
                                </div>
                            </div>

                            <div className="bg-gray-50 p-4 rounded-xl flex items-center justify-between border border-gray-100">
                                <span className="font-medium text-gray-600">Amount</span>
                                <span className={`text-2xl font-bold ${
                                    (selectedTransaction.type === 'expense' && !selectedTransaction.isMyExpense) ||
                                    selectedTransaction.type === 'tournament' ||
                                    selectedTransaction.type === 'umpiring' ||
                                    (selectedTransaction.amount < 0 && selectedTransaction.type === 'due')
                                    ? 'text-red-600'
                                    : 'text-green-600'
                                }`}>
                                    {selectedTransaction.amount < 0 ? '-' : (selectedTransaction.type === 'expense' || selectedTransaction.type === 'transaction') ? '+' : ''}
                                    {formatCurrency(Math.abs(selectedTransaction.amount))}
                                </span>
                            </div>

                            {/* Additional metadata depending on type */}
                            {selectedTransaction.original?.receiptURL && (
                                <div>
                                    <p className="text-sm text-gray-500 font-medium mb-2">Receipt</p>
                                    <a
                                        href={selectedTransaction.original.receiptURL}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-2 px-3 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-semibold hover:bg-blue-100 transition-colors"
                                    >
                                        <FileText size={16} />
                                        View Receipt
                                    </a>
                                </div>
                            )}

                            {selectedTransaction.original?.transactionId && (
                                <div>
                                    <p className="text-sm text-gray-500 font-medium mb-1">Reference ID</p>
                                    <p className="font-mono text-sm text-gray-700 break-all bg-gray-50 px-3 py-2 rounded-lg border border-gray-100">
                                        {selectedTransaction.original.transactionId}
                                    </p>
                                </div>
                            )}
                            
                            {selectedTransaction.original?.paymentMethod && (
                                <div>
                                    <p className="text-sm text-gray-500 font-medium mb-1">Payment Method</p>
                                    <p className="font-medium text-gray-800 capitalize">
                                        {selectedTransaction.original.paymentMethod}
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
        </div>
    );
};

export default MobileDuesView;
